import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ArrowLeft, Plus, Minus, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Food", "Drinks", "Snacks", "Combos"];
const SIDE_OPTIONS = ["Fries", "Chips"];
const DRINK_OPTIONS = ["Soda Bottle", "Can Soda", "Powerade", "Tea", "Water"];

export default function Menu() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [sidePicker, setSidePicker] = useState(null); // combo/variant/addons item awaiting choices
  const [pickedSide, setPickedSide] = useState(null);
  const [pickedDrink, setPickedDrink] = useState(null);
  const [pickedVariant, setPickedVariant] = useState(null);
  // Map of addon label → quantity chosen for the current picker session.
  const [pickedAddons, setPickedAddons] = useState({});
  const { addItem, items: cartItems, count, total, updateQuantity } = useCart();
  const prevAvailability = useRef({});
  const [recentlySoldOut, setRecentlySoldOut] = useState(new Set());
  const [settings, setSettings] = useState({ orders_open: true, active_sport: "" });

  const refresh = () => {
    // Fetch menu + settings in parallel so the pause banner and menu land together.
    Promise.all([api.get("/menu"), api.get("/settings")])
      .then(([mRes, sRes]) => {
        const next = mRes.data;
        // Detect items that transitioned available → sold out and flash them red.
        const justFlipped = new Set();
        for (const it of next) {
          const wasAvailable = prevAvailability.current[it.id];
          if (wasAvailable === true && it.available === false) {
            justFlipped.add(it.id);
          }
          prevAvailability.current[it.id] = it.available;
        }
        if (justFlipped.size > 0) {
          setRecentlySoldOut(justFlipped);
          setTimeout(() => setRecentlySoldOut(new Set()), 4000);
        }
        setItems(next);
        setSettings(sRes.data);
      })
      .catch(() => toast.error("Couldn't load menu"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // Poll every 20s so customers see live sold-out flips from the admin.
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      activeCat === "All"
        ? items
        : items.filter((i) => i.category === activeCat),
    [items, activeCat]
  );

  const qtyInCart = (id) =>
    cartItems
      .filter((c) => (c.item_id || c.id) === id)
      .reduce((a, c) => a + c.quantity, 0);

  const handleAdd = (item) => {
    const needsCombo = item.category === "Combos";
    const needsVariant = Array.isArray(item.variants) && item.variants.length > 0;
    const hasAddons = Array.isArray(item.addons) && item.addons.length > 0;
    if (needsCombo || needsVariant || hasAddons) {
      setPickedSide(null);
      setPickedDrink(null);
      setPickedVariant(null);
      setPickedAddons({});
      setSidePicker(item);
      return;
    }
    addItem({ id: item.id, item_id: item.id, name: item.name, price: item.price });
    toast.success(`${item.name} added`, { duration: 1500 });
  };

  const confirmCombo = () => {
    const item = sidePicker;
    if (!item) return;
    const needsCombo = item.category === "Combos";
    const needsVariant = Array.isArray(item.variants) && item.variants.length > 0;
    if (needsCombo && (!pickedSide || !pickedDrink)) return;
    if (needsVariant && !pickedVariant) return;
    // Build validated add-ons list (only non-zero qty entries).
    const chosenAddons = (item.addons || [])
      .map((a) => ({ label: a.label, price: Number(a.price) || 0, qty: pickedAddons[a.label] || 0 }))
      .filter((a) => a.qty > 0);
    setSidePicker(null);
    // Build a stable cart key that reflects every choice — same base item with
    // different choices is treated as a distinct line.
    const addonKey = chosenAddons.map((a) => `${a.label}×${a.qty}`).join(",");
    const cartKey = [item.id, pickedVariant, pickedSide, pickedDrink, addonKey]
      .filter(Boolean).join("|");
    const addonTotal = chosenAddons.reduce((sum, a) => sum + a.price * a.qty, 0);
    const suffixParts = [pickedVariant, pickedSide, pickedDrink].filter(Boolean);
    if (chosenAddons.length) {
      suffixParts.push(
        chosenAddons.map((a) => (a.qty > 1 ? `+${a.label}×${a.qty}` : `+${a.label}`)).join(" ")
      );
    }
    const suffix = suffixParts.join(" · ");
    addItem({
      cart_key: cartKey,
      item_id: item.id,
      id: item.id,
      name: suffix ? `${item.name} (${suffix})` : item.name,
      price: Math.round((item.price + addonTotal) * 100) / 100,
      side_choice: pickedSide || undefined,
      drink_choice: pickedDrink || undefined,
      variant_choice: pickedVariant || undefined,
      addons: chosenAddons.length ? chosenAddons : undefined,
    });
    toast.success(`${item.name} added`, { duration: 1500 });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-32">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b-2 border-slate-900 bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between px-5 py-4 sm:px-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em]">Back</span>
          </button>
          <div className="text-center">
            <div className="font-heading text-xl tracking-wider">CONCESSIONS</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {settings.active_sport ? `Tonight · ${settings.active_sport}` : "Cowboys Express"}
            </div>
          </div>
          <div className="w-12" />
        </div>

        {/* Orders paused banner — shown when admin has toggled orders off */}
        {!settings.orders_open && !loading && (
          <div
            data-testid="orders-paused-banner"
            className="border-t-2 border-red-600 bg-red-50 px-4 py-3 text-center"
          >
            <div className="font-heading text-sm tracking-[0.25em] text-red-700">
              ORDERS CURRENTLY PAUSED
            </div>
            <div className="text-[11px] text-red-600/80 mt-0.5">
              Check back before the next game — see you at the concession stand!
            </div>
          </div>
        )}

        <div className="flex gap-0 overflow-x-auto border-t border-slate-200 px-2 sm:px-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c.toLowerCase()}-btn`}
              onClick={() => setActiveCat(c)}
              className={`px-4 py-3 font-heading text-base tracking-wider transition-colors whitespace-nowrap ${
                activeCat === c
                  ? "text-[#1d4ed8] border-b-2 border-[#1d4ed8]"
                  : "text-slate-500 border-b-2 border-transparent hover:text-slate-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-8">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse border border-slate-200 bg-slate-100"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-500">
            No items available.
          </div>
        ) : (
          <div className="border-2 border-slate-900">
            {filtered.map((item, idx) => {
              const q = qtyInCart(item.id);
              const soldOut = item.available === false;
              const justSoldOut = recentlySoldOut.has(item.id);
              const disabled = soldOut || !settings.orders_open;
              return (
                <div
                  key={item.id}
                  data-testid={`menu-item-${item.id}`}
                  data-soldout={soldOut ? "true" : "false"}
                  className={`relative flex items-stretch bg-white ${
                    idx !== filtered.length - 1
                      ? "border-b border-slate-200"
                      : ""
                  } ${soldOut ? "opacity-60" : ""} ${justSoldOut ? "sold-out-pulse" : ""}`}
                >
                  <div
                    className={`h-28 w-28 flex-shrink-0 bg-cover bg-center sm:h-32 sm:w-32 ${soldOut ? "grayscale" : ""}`}
                    style={{
                      backgroundImage: item.image_url
                        ? `url(${item.image_url})`
                        : undefined,
                      backgroundColor: "#e2e8f0",
                    }}
                  />
                  <div className="flex flex-1 items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">
                          {item.category}
                        </div>
                        {soldOut && (
                          <span
                            data-testid={`sold-out-badge-${item.id}`}
                            className="bg-red-600 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white"
                          >
                            SOLD OUT
                          </span>
                        )}
                      </div>
                      <div className="font-heading text-lg leading-tight tracking-wide sm:text-xl text-slate-900">
                        {item.name}
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-slate-500 sm:text-sm">
                        {item.description}
                      </div>
                      <div className="mt-2 font-heading text-xl text-[#1d4ed8] tabular">
                        ${item.price.toFixed(2)}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {disabled ? (
                        <button
                          data-testid={soldOut ? `sold-out-btn-${item.id}` : `paused-btn-${item.id}`}
                          disabled
                          className="flex h-11 w-11 items-center justify-center bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed"
                          aria-label={soldOut ? `${item.name} sold out` : "Orders paused"}
                        >
                          <Plus className="h-5 w-5" strokeWidth={3} />
                        </button>
                      ) : item.category === "Combos" || q === 0 ? (
                        <button
                          data-testid={`add-item-${item.id}-btn`}
                          onClick={() => handleAdd(item)}
                          className="flex h-11 w-11 items-center justify-center bg-[#1d4ed8] text-white transition-all hover:bg-[#1e40af] active:scale-95 border border-slate-900"
                          aria-label={`Add ${item.name}`}
                        >
                          <Plus className="h-5 w-5" strokeWidth={3} />
                        </button>
                      ) : (
                        <div className="flex items-center border-2 border-[#1d4ed8]">
                          <button
                            data-testid={`dec-item-${item.id}-btn`}
                            onClick={() => updateQuantity(item.id, q - 1)}
                            className="flex h-11 w-11 items-center justify-center text-[#1d4ed8] hover:bg-blue-50"
                          >
                            <Minus className="h-4 w-4" strokeWidth={3} />
                          </button>
                          <div
                            data-testid={`qty-item-${item.id}`}
                            className="w-9 text-center font-heading text-lg text-[#1d4ed8] tabular"
                          >
                            {q}
                          </div>
                          <button
                            data-testid={`inc-item-${item.id}-btn`}
                            onClick={() => handleAdd(item)}
                            className="flex h-11 w-11 items-center justify-center bg-[#1d4ed8] text-white hover:bg-[#1e40af]"
                          >
                            <Plus className="h-5 w-5" strokeWidth={3} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Sticky cart drawer */}
      {count > 0 && (
        <div
          data-testid="cart-bar"
          className={`fixed bottom-0 left-0 right-0 z-40 border-t-2 border-slate-900 text-white ${
            settings.orders_open ? "bg-[#1d4ed8]" : "bg-slate-400"
          }`}
        >
          <button
            data-testid="checkout-btn"
            onClick={() => {
              if (!settings.orders_open) {
                toast.error("Orders are paused right now");
                return;
              }
              navigate("/checkout");
            }}
            disabled={!settings.orders_open}
            className={`flex w-full items-center justify-between px-5 py-4 transition-all sm:px-8 ${
              settings.orders_open ? "active:bg-[#1e40af]" : "cursor-not-allowed"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingBag className="h-7 w-7" strokeWidth={2.5} />
                <span
                  data-testid="cart-count"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center bg-white font-heading text-xs text-[#1d4ed8]"
                >
                  {count}
                </span>
              </div>
              <div className="text-left">
                <div className="font-heading text-base tracking-wider">
                  {settings.orders_open ? "REVIEW & PAY" : "ORDERS PAUSED"}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-200">
                  {count} {count === 1 ? "item" : "items"}
                </div>
              </div>
            </div>
            <div className="font-heading text-2xl tabular">${total.toFixed(2)}</div>
          </button>
        </div>
      )}
      {/* Combo / variant / add-ons picker */}
      {sidePicker && (() => {
        const needsCombo = sidePicker.category === "Combos";
        const needsVariant = Array.isArray(sidePicker.variants) && sidePicker.variants.length > 0;
        const itemAddons = Array.isArray(sidePicker.addons) ? sidePicker.addons : [];
        const hasAddons = itemAddons.length > 0;
        const ready =
          (!needsCombo || (pickedSide && pickedDrink)) &&
          (!needsVariant || pickedVariant);
        const addonTotal = itemAddons.reduce(
          (sum, a) => sum + (Number(a.price) || 0) * (pickedAddons[a.label] || 0),
          0
        );
        const runningPrice = (sidePicker.price + addonTotal).toFixed(2);
        const bumpAddon = (label, max_qty, delta) => {
          const cap = Math.max(1, Math.min(10, parseInt(max_qty, 10) || 1));
          setPickedAddons((prev) => {
            const next = { ...prev };
            const current = next[label] || 0;
            const target = Math.max(0, Math.min(cap, current + delta));
            if (target === 0) delete next[label];
            else next[label] = target;
            return next;
          });
        };
        const closePicker = () => {
          setSidePicker(null);
          setPickedSide(null);
          setPickedDrink(null);
          setPickedVariant(null);
          setPickedAddons({});
        };
        return (
        <div
          data-testid="side-picker"
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 sm:items-center"
          onClick={closePicker}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md max-h-[90vh] overflow-y-auto border-2 border-slate-900 bg-white p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
                  {needsCombo ? "Build Your Combo" : needsVariant ? "Choose a Style" : "Customize"}
                </div>
                <div className="font-heading text-2xl tracking-wider">
                  {sidePicker.name}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {needsCombo && needsVariant && "Pick style, side & drink"}
                  {needsCombo && !needsVariant && "Pick a side and a drink — both included"}
                  {!needsCombo && needsVariant && "Pick how you'd like it prepared"}
                  {!needsCombo && !needsVariant && hasAddons && "Add extras if you like"}
                </div>
              </div>
              <button
                data-testid="side-picker-close"
                onClick={closePicker}
                className="text-slate-400 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Variant selection (e.g. Grilled / Breaded) */}
            {needsVariant && (
              <div className="mt-5" data-testid="variant-section">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Style
                </div>
                <div className={`grid gap-2 ${sidePicker.variants.length > 2 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {sidePicker.variants.map((opt) => {
                    const active = pickedVariant === opt;
                    return (
                      <button
                        key={opt}
                        data-testid={`variant-${opt.toLowerCase()}-btn`}
                        onClick={() => setPickedVariant(opt)}
                        className={`border-2 py-3 font-heading text-lg tracking-wider transition-all active:scale-95 ${
                          active
                            ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                            : "border-slate-900 bg-white text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        {opt.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Side selection (combo only) */}
            {needsCombo && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Side
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {SIDE_OPTIONS.map((opt) => {
                    const active = pickedSide === opt;
                    return (
                      <button
                        key={opt}
                        data-testid={`side-${opt.toLowerCase()}-btn`}
                        onClick={() => setPickedSide(opt)}
                        className={`border-2 py-3 font-heading text-lg tracking-wider transition-all active:scale-95 ${
                          active
                            ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                            : "border-slate-900 bg-white text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        {opt.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Drink selection (combo only) */}
            {needsCombo && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Drink
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {DRINK_OPTIONS.map((opt) => {
                    const active = pickedDrink === opt;
                    const testid = `drink-${opt.toLowerCase().replace(/\s+/g, "-")}-btn`;
                    return (
                      <button
                        key={opt}
                        data-testid={testid}
                        onClick={() => setPickedDrink(opt)}
                        className={`border-2 py-2.5 px-2 font-heading text-sm tracking-wider transition-all active:scale-95 ${
                          active
                            ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                            : "border-slate-900 bg-white text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        {opt.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add-ons — optional stackable extras */}
            {hasAddons && (
              <div className="mt-5" data-testid="addons-section">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Add-ons
                </div>
                <div className="space-y-2">
                  {itemAddons.map((a) => {
                    const qty = pickedAddons[a.label] || 0;
                    const cap = Math.max(1, parseInt(a.max_qty, 10) || 1);
                    const price = Number(a.price) || 0;
                    const testKey = (a.label || "").toLowerCase().replace(/\s+/g, "-");
                    return (
                      <div
                        key={a.label}
                        data-testid={`addon-row-${testKey}`}
                        className={`flex items-center justify-between border-2 px-3 py-2 ${qty > 0 ? "border-[#1d4ed8] bg-blue-50" : "border-slate-200 bg-white"}`}
                      >
                        <div className="min-w-0">
                          <div className="font-heading text-sm tracking-wider text-slate-900 truncate">
                            {a.label}
                          </div>
                          <div className="text-[10px] text-slate-500 tabular">
                            +${price.toFixed(2)}{cap > 1 ? ` · up to ${cap}` : ""}
                          </div>
                        </div>
                        {cap === 1 ? (
                          <button
                            data-testid={`addon-toggle-${testKey}`}
                            onClick={() => bumpAddon(a.label, cap, qty ? -1 : 1)}
                            className={`border-2 px-3 py-1.5 font-heading text-[11px] tracking-wider transition-all active:scale-95 ${
                              qty
                                ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                                : "border-slate-900 bg-white text-slate-900 hover:bg-slate-100"
                            }`}
                          >
                            {qty ? "ADDED" : "ADD"}
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`addon-minus-${testKey}`}
                              onClick={() => bumpAddon(a.label, cap, -1)}
                              disabled={qty === 0}
                              className="border-2 border-slate-900 bg-white h-8 w-8 font-heading text-lg leading-none hover:bg-slate-100 disabled:opacity-30"
                              aria-label={`Remove ${a.label}`}
                            >
                              −
                            </button>
                            <span
                              data-testid={`addon-qty-${testKey}`}
                              className="font-heading text-base tabular w-6 text-center"
                            >
                              {qty}
                            </span>
                            <button
                              data-testid={`addon-plus-${testKey}`}
                              onClick={() => bumpAddon(a.label, cap, 1)}
                              disabled={qty >= cap}
                              className="border-2 border-slate-900 bg-white h-8 w-8 font-heading text-lg leading-none hover:bg-slate-100 disabled:opacity-30"
                              aria-label={`Add ${a.label}`}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              data-testid="combo-confirm-btn"
              onClick={confirmCombo}
              disabled={!ready}
              className="mt-5 w-full bg-[#1d4ed8] py-3 font-heading text-lg tracking-wider text-white hover:bg-[#1e40af] disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {ready
                ? `ADD TO CART · $${runningPrice}`
                : needsVariant && !pickedVariant
                  ? (needsCombo ? "PICK STYLE, SIDE & DRINK" : `PICK ${sidePicker.variants.join(" OR ")}`.toUpperCase())
                  : "PICK SIDE & DRINK"}
            </button>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
